import type { Abstract } from './valueObjects/abstract';
import type { CategoryId } from './valueObjects/categoryId';
import type { CreateDatetime } from './valueObjects/createDatetime';
import type { LastModifiedDatetime } from './valueObjects/lastModifiedDatetime';
import type { MemberId } from './valueObjects/memberId';
import type { PublishDatetime } from './valueObjects/publishDatetime';
import type { Title } from './valueObjects/title';
import type { PrivateBlogJson } from '@/types/domain/privateBlog/entities/privateBlogEntity';

import { Entity } from '@/domain/entity';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * ホームページプロパティ
 */
export type PrivateBlogProps = {
  /** 作成日時 */
  createDatetime: CreateDatetime,
  /** 作成メンバーID */
  createMemberId: MemberId,
  /** 最終更新日時 */
  lastModifiedDatetime: LastModifiedDatetime,
  /** 最終更新者ID */
  lastModifiedMemberId: MemberId,
  /** 公開日時 */
  publishDatetime: PublishDatetime,
  /** 公開メンバーID */
  publishMemberId: MemberId,
  /** タイトル */
  title: Title,
  /** カテゴリID */
  categoryId: CategoryId,
  /** 要約 */
  abstract: Abstract,
  /** 画像数 */
  numOfImg: number,
}

/**
 * ホームページEntity
 */
export class PrivateBlogEntity extends Entity<
  PrivateBlogProps,
  PrivateBlogJson
> {
  /**
   * 削除タイプ
   */
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  /**
   * 削除キー
   */
  get deleteKey(): string {
    return this.getFutureYear(this.publishDatetime.value, 3).toJSON();
  }

  /**
   * データJsonを返す
   * @returns データ
   */
  toJsonData(): PrivateBlogJson {
    return {
      createDatetime: this.createDatetime.value.toJSON(),
      createMemberId: this.createMemberId.value,
      lastModifiedDatetime: this.lastModifiedDatetime.value.toJSON(),
      lastModifiedMemberId: this.lastModifiedMemberId.value,
      publishDatetime: this.publishDatetime.value.toJSON(),
      publishMemberId: this.publishMemberId.value,
      title: this.title.value,
      categoryId: this.categoryId.value,
      abstract: this.abstract.value,
      numOfImg: this.numOfImg,
      s3ThumbnailPath: this.s3ThumbnailPath,
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryPrivateBlogPrivateBlogEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.categoryId}#${this.publishDatetime.toStringDateTime()}`;
  }

  /**
   * サブキー
   */
  get subKey() {
    return `${this.categoryId.value}#${this.title.value}`;
  }

  /**
   * S3ファイル名
   */
  get s3Filename(): string {
    return 'd.json.gz';
  }

  /**
   * S3サムネイル画像名
   */
  get s3ThumbnailFilename(): string {
    return 'd.jpg';
  }

  /**
   * S3パス
   */
  get s3Path(): string {
    const name = this.entityName;
    const key = this.entityKey;
    const fname = this.s3Filename;
    return `${name}/${key}/${fname}`;
  }

  /**
   * S3サムネイル画像パス
   */
  get s3ThumbnailPath(): string {
    if (this.numOfImg === 0) {
      return '';
    }
    const name = this.entityName;
    const key = this.entityKey;
    const fname = this.s3ThumbnailFilename;
    return `${name}/${key}/${fname}`;
  }

  /**
   * 作成日時
   */
  get createDatetime(): CreateDatetime {
    return this._value.createDatetime;
  }

  /**
   * 作成日時
   */
  set createDatetime(createDatetime: CreateDatetime) {
    this._value.createDatetime = createDatetime;
  }

  /**
   * 作成メンバーID
   */
  get createMemberId(): MemberId {
    return this._value.createMemberId;
  }

  /**
   * 作成メンバーID
   */
  set createMemberId(createMemberId: MemberId) {
    this._value.createMemberId = createMemberId;
  }

  /**
   * NurseryID
   */
  get nurseryId(): string {
    return this._value.createMemberId.value.split('_')[0];
  }

  /**
   * 最終更新日時
   */
  get lastModifiedDatetime(): LastModifiedDatetime {
    return this._value.lastModifiedDatetime;
  }

  /**
   * 最終更新日時
   */
  set lastModifiedDatetime(lastModifiedDatetime: LastModifiedDatetime) {
    this._value.lastModifiedDatetime = lastModifiedDatetime;
  }

  /**
   * 最終更新者ID
   */
  get lastModifiedMemberId(): MemberId {
    return this._value.lastModifiedMemberId;
  }

  /**
   * 最終更新者ID
   */
  set lastModifiedMemberId(lastModifiedMemberId: MemberId) {
    this._value.lastModifiedMemberId = lastModifiedMemberId;
  }

  /**
   * 公開日時
   */
  get publishDatetime(): PublishDatetime {
    return this._value.publishDatetime;
  }

  /**
   * 公開日時
   */
  set publishDatetime(publishDatetime: PublishDatetime) {
    this._value.publishDatetime = publishDatetime;
  }

  /**
   * 公開メンバーID
   */
  get publishMemberId(): MemberId {
    return this._value.publishMemberId;
  }

  /**
   * 公開メンバーID
   */
  set publishMemberId(publishMemberId: MemberId) {
    this._value.publishMemberId = publishMemberId;
  }

  /**
   * タイトル
   */
  get title(): Title {
    return this._value.title;
  }

  /**
   * タイトル
   */
  set title(title: Title) {
    this._value.title = title;
  }

  /**
   * カテゴリID
   */
  get categoryId(): CategoryId {
    return this._value.categoryId;
  }

  /**
   * カテゴリID
   */
  set categoryId(categoryId: CategoryId) {
    this._value.categoryId = categoryId;
  }

  /**
   * 要約
   */
  get abstract(): Abstract {
    return this._value.abstract;
  }

  /**
   * 要約
   */
  set abstract(abstract: Abstract) {
    this._value.abstract = abstract;
  }

  /**
   * 画像数
   */
  get numOfImg(): number {
    return this._value.numOfImg;
  }

  /**
   * 画像数
   */
  set numOfImg(numOfImg: number) {
    this._value.numOfImg = numOfImg;
  }
}
